import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type {
  SlackChannelEntity,
  SlackUserConversationsListInput,
} from "@cohesive/api-entities";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useMemo } from "react";
import { buildGenericById, toGenericArray } from "../../genericProjection";

interface UseSlackUserConversationsListProps {
  connection?: IntegrationConnection;
  limit?: number;
  cursor?: string;
  exclude_archived?: boolean;
  types?: string;
  options?: Partial<
    UseQueryOptions<ExecuteToolboxResponse<SlackChannelEntity[]>, Error>
  >;
}

export function useSlackUserConversationsList({
  connection,
  limit = 100,
  cursor,
  exclude_archived = true,
  types = "public_channel,private_channel,im,mpim",
  options = {},
}: UseSlackUserConversationsListProps) {
  const query = useQuery<ExecuteToolboxResponse<SlackChannelEntity[]>>({
    queryKey: [
      "slack-user-conversations-list",
      connection?.id,
      limit,
      cursor,
      exclude_archived,
      types,
    ],
    queryFn: async () => {
      const input: SlackUserConversationsListInput = {
        limit,
        exclude_archived,
        types,
      };
      if (cursor) input.cursor = cursor;

      // Typed channels drive the tree UI; their generic projections ride along
      // in data_generic and are what get placed on the canvas.
      return orgAPI.toolbox.execute<SlackChannelEntity[], any>({
        integration_connection_id: connection?.id!,
        action_name: "slack-user-conversations-list",
        action_input: input,
        entity_format: "both",
      });
    },
    enabled: !!connection?.integration_id,
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });

  // Channel generics keyed by id, so the sidebar can place a generic channel
  // while the typed channels drive the tree. `data` stays the typed array, so
  // existing consumers (e.g. SlackChannelControl) are unaffected.
  const genericById = useMemo(
    () => buildGenericById(toGenericArray(query.data?.data_generic)),
    [query.data],
  );

  return { ...query, data: query.data?.data, genericById };
}
